import Swal from "sweetalert2";
import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import LayersIcon from '@mui/icons-material/Layers';
import ScienceIcon from '@mui/icons-material/Science';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * ReprocesoSection (Sección VI - Bitácora de Envasado)
 * Elaboración de Clases, Reprocesos y Reacondicionamientos para Envasado NOM-070-SCFI-2016.
 * Homogéneo en diseño, validaciones y funcionamiento con la Sección VI de Producción.
 */
const ReprocesoSection = ({
  reprocesoRecords = [],
  granelRecords = [],
  allGranelRecords = [],
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveReproceso,
  handleDeleteReproceso,
  onReloadReproceso,
  isRefreshingReproceso = false
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isLocalRefreshing, setIsLocalRefreshing] = useState(false);

  const handleReload = async () => {
    if (onReloadReproceso) {
      try {
        setIsLocalRefreshing(true);
        await onReloadReproceso();
      } catch (err) {
        console.error("Error al actualizar reproceso:", err);
      } finally {
        setTimeout(() => setIsLocalRefreshing(false), 500);
      }
    }
  };

  // Filtrar los graneles de Envasado disponibles con saldo disponible mayor a 0
  const availableGraneles = useMemo(() => {
    const list = (Array.isArray(allGranelRecords) && allGranelRecords.length > 0)
      ? allGranelRecords
      : (Array.isArray(granelRecords) ? granelRecords : []);

    return list.filter(g => {
      const vol = g._vDisp ?? (parseFloat(String(g.invFinalVol ?? g.saldoFinal ?? g.litros ?? g.volumen ?? 0).replace(/[^\d.]/g, '')) || 0);
      return vol > 0;
    });
  }, [allGranelRecords, granelRecords]);

  /**
   * Define la Clase Final Obtenida según la NOM-070-SCFI-2016 con base en el Proceso Realizado:
   * - “ABOCADO CON” -> “ABOCADO CON”
   * - “DESTILADO CON” -> “DESTILADO CON”
   * - “REPROCESO O REACONDICIONAMIENTO” -> Clase origen o “BLANCO O JOVEN”
   */
  const obtenerClaseFinalSegunProceso = (proceso, claseOrigen = "BLANCO O JOVEN") => {
    const p = String(proceso || "").toUpperCase().trim();
    if (p.includes("ABOCADO")) {
      return "ABOCADO CON";
    }
    if (p.includes("DESTILADO")) {
      return "DESTILADO CON";
    }
    if (p.includes("REPROCESO") || p.includes("REACONDICIONAMIENTO")) {
      return String(claseOrigen || "BLANCO O JOVEN").toUpperCase();
    }
    return "BLANCO O JOVEN";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "procesoRealizado") {
      const autoClase = obtenerClaseFinalSegunProceso(value, formData?.claseOrigen);
      setFormData(prev => ({
        ...prev,
        procesoRealizado: value,
        claseSalida: autoClase
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Manejador al seleccionar un lote de granel de envasado
  const handleLoteOrigenChange = (e) => {
    const val = e.target.value;
    const found = availableGraneles.find(g =>
      String(g.noLote || g.no_lote) === val ||
      String(g.id_bitacora_granel) === val ||
      String(g.id) === val ||
      String(g.dbId) === val
    );

    if (found) {
      const loteNo = found.noLote || found.no_lote || val;
      const vol = found._vDisp ?? (parseFloat(String(found.invFinalVol ?? found.saldoFinal ?? found.litros ?? found.volumen ?? 0).replace(/[^\d.]/g, '')) || 0);
      const alc = found._aDisp ?? (parseFloat(String(found.porcentaje_alc_vol ?? found.invFinalAlc ?? 0).replace(/[^\d.]/g, '')) || 0);
      const cat = found.categoria || "Mezcal Artesanal";
      const cls = found.clase || "Blanco Joven";
      const mag = found.tipoMaguey || found.especie || "";
      const anl = found.clave_fq || found.noAnalisis || found.noAnalisisFisicoquimico || "";
      const ing = found.ingredientes || found.ingrediente || "Sin ingredientes adicionales";
      const edd = found.edad || "Sin edad / 0 meses";
      const granelId = found.id_bitacora_granel || (typeof found.id === "number" ? found.id : null);

      const autoClase = obtenerClaseFinalSegunProceso(formData?.procesoRealizado || "ABOCADO CON", cls);
      const inspId = found.id_inspector || found.idinspector || null;
      const inspName = found.inspector || found.vbo_inspector || "";

      setFormData(prev => ({
        ...prev,
        id_bitacora_granel: granelId,
        id_inspector: inspId || prev.id_inspector,
        inspector: (inspName && inspName !== "Sin asignar") ? inspName : prev.inspector,
        loteOrigen: loteNo,
        volumenOrigen: vol || prev.volumenOrigen || "",
        pctAlcOrigen: alc || prev.pctAlcOrigen || "",
        categoriaOrigen: cat || prev.categoriaOrigen || "Mezcal Artesanal",
        claseOrigen: cls || prev.claseOrigen || "Blanco Joven",
        tipoMagueyOrigen: mag || prev.tipoMagueyOrigen || "",
        analisisOrigen: anl || prev.analisisOrigen || "",
        ingredientesOrigen: ing || prev.ingredientesOrigen || "",
        edadOrigen: edd || prev.edadOrigen || "",
        loteSalida: prev.loteSalida || (loteNo ? `${loteNo}-REP` : ""),
        claseSalida: autoClase
      }));
    } else {
      setFormData(prev => ({ ...prev, loteOrigen: val, id_bitacora_granel: null }));
    }
  };

  const handleNewRecord = () => {
    const today = new Date().toISOString().split("T")[0];
    const defaultProceso = "ABOCADO CON";
    setFormData({
      id: `REP-${Date.now()}`,
      fechaIngreso: today,
      loteOrigen: "",
      volumenOrigen: "",
      pctAlcOrigen: "",
      categoriaOrigen: "Mezcal Artesanal",
      claseOrigen: "Blanco Joven",
      ingredientesOrigen: "",
      edadOrigen: "",
      analisisOrigen: "",
      tipoMagueyOrigen: "",
      ingredientesAdicionados: "",
      procesoRealizado: defaultProceso,
      descripcionProceso: "",
      fechaSalida: today,
      loteSalida: "",
      volumenSalida: "",
      pctAlcSalida: "",
      claseSalida: obtenerClaseFinalSegunProceso(defaultProceso),
      observaciones: "",
      status: "borrador"
    });
    setIsFormOpen(true);
  };

  const handleEditRecord = (rec) => {
    const rawStatus = (rec.status || rec.estatus || rec.statusUI || "").toLowerCase();
    if (rawStatus !== "borrador" && rawStatus !== "rechazado" && rawStatus !== "rechazada") {
      Swal.fire({
        title: "Registro no editable",
        text: "Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADO.",
        icon: "info",
        confirmButtonColor: "#2563EB"
      });
      return;
    }
    const proc = rec.procesoRealizado || rec.proceso_realizado || "ABOCADO CON";
    const idRep = rec.id_bitacora_reproceso || rec.id || rec.dbId;
    setFormData({
      id: rec.id || idRep,
      dbId: idRep,
      id_bitacora_reproceso: idRep,
      fechaIngreso: rec.fechaIngreso || rec.fecha_ingreso || "",
      id_bitacora_granel: rec.id_bitacora_granel || null,
      loteOrigen: rec.loteOrigen || rec.lote_origen || "",
      volumenOrigen: rec.volumenOrigen !== undefined ? String(rec.volumenOrigen).replace(/[^\d.]/g, '') : "",
      pctAlcOrigen: rec.pctAlcOrigen !== undefined ? String(rec.pctAlcOrigen).replace(/[^\d.]/g, '') : "",
      categoriaOrigen: rec.categoriaOrigen || rec.categoria_origen || "Mezcal Artesanal",
      claseOrigen: rec.claseOrigen || rec.clase_origen || "Blanco Joven",
      tipoMagueyOrigen: rec.tipoMagueyOrigen || rec.tipo_maguey_origen || "",
      analisisOrigen: rec.analisisOrigen || rec.analisis_origen || "",
      ingredientesOrigen: rec.ingredientesOrigen || rec.ingredientes_origen || "",
      edadOrigen: rec.edadOrigen || rec.edad_origen || "",
      procesoRealizado: proc,
      ingredientesAdicionados: rec.ingredientesAdicionados || rec.ingredientes_adicionados || "",
      descripcionProceso: rec.descripcionProceso || rec.descripcion_proceso || "",
      fechaSalida: rec.fechaSalida || rec.fecha_salida || "",
      loteSalida: rec.loteSalida || rec.lote_salida || "",
      volumenSalida: rec.volumenSalida !== undefined ? String(rec.volumenSalida).replace(/[^\d.]/g, '') : "",
      pctAlcSalida: rec.pctAlcSalida !== undefined ? String(rec.pctAlcSalida).replace(/[^\d.]/g, '') : "",
      claseSalida: rec.claseSalida || rec.clase_salida || obtenerClaseFinalSegunProceso(proc, rec.claseOrigen || rec.clase_origen),
      observaciones: rec.observaciones || "",
      status: rawStatus === "borrador" ? "borrador" : rawStatus,
      estatus: rawStatus === "borrador" ? "borrador" : rawStatus
    });
    setIsFormOpen(true);
  };

  const handleSubmitForm = (statusToSet = "revision") => {
    // Si se envía para registrar la sección (a revisión), validar campos obligatorios
    if (statusToSet === "revision") {
      const faltantes = [];
      if (!formData?.fechaIngreso) faltantes.push("Fecha de Ingreso");
      if (!formData?.volumenOrigen || Number(formData?.volumenOrigen) <= 0) faltantes.push("Volumen Inicial (mayor a 0 L)");
      if (!formData?.procesoRealizado) faltantes.push("Proceso Realizado");
      if (!formData?.loteSalida || !String(formData?.loteSalida).trim()) faltantes.push("Lote Final Obtenido");
      if (!formData?.volumenSalida || Number(formData?.volumenSalida) <= 0) faltantes.push("Volumen Final (mayor a 0 L)");

      if (faltantes.length > 0) {
        Swal.fire({
          title: "Campos Obligatorios Incompletos",
          html: `
            <div style="text-align: left; font-size: 13.5px;">
              <p>Para <strong>Registrar la Sección VI</strong> y enviarla a revisión oficial, debe completar los siguientes campos obligatorios:</p>
              <ul style="color: #b91c1c; margin-bottom: 12px; line-height: 1.6;">
                ${faltantes.map(f => `<li><strong>${f}</strong></li>`).join("")}
              </ul>
              <p style="font-size: 12px; color: #64748B;">
                💡 <em>Nota: Si aún no tiene todos los datos completos, puede hacer clic en <strong>Guardar Borrador</strong>.</em>
              </p>
            </div>
          `,
          icon: "warning",
          confirmButtonColor: "#2563EB"
        });
        return;
      }

      // Validar que el volumen origen no exceda el inventario disponible si se seleccionó un lote de granel
      if (formData?.loteOrigen) {
        const found = availableGraneles.find(g =>
          String(g.noLote || g.no_lote) === String(formData.loteOrigen) ||
          String(g.id_bitacora_granel) === String(formData.id_bitacora_granel)
        );
        if (found) {
          const maxVol = found._vDisp ?? (parseFloat(String(found.invFinalVol ?? found.saldoFinal ?? found.litros ?? found.volumen ?? 0).replace(/[^\d.]/g, '')) || 0);
          if (maxVol > 0 && Number(formData.volumenOrigen) > maxVol) {
            Swal.fire({
              title: "Volumen Excede Inventario Disponible",
              text: `El volumen a reprocesar (${formData.volumenOrigen} L) supera el saldo disponible actual (${maxVol} L) del lote ${formData.loteOrigen}.`,
              icon: "error",
              confirmButtonColor: "#DC2626"
            });
            return;
          }
        }
      }
    }

    const finalClase = obtenerClaseFinalSegunProceso(formData?.procesoRealizado || "ABOCADO CON", formData?.claseOrigen);
    const recordToSave = {
      ...formData,
      claseSalida: finalClase,
      status: statusToSet,
      estatus: statusToSet,
      inspector: formData?.inspector || (statusToSet === "revision" ? "Enviado a revisión" : "Borrador de usuario")
    };

    if (handleSaveReproceso) {
      handleSaveReproceso(recordToSave);
    }
    setIsFormOpen(false);
  };

  return (
    <div className="animate-fadeIn">
      {/* HEADER DE LA SECCIÓN CON BOTÓN NUEVO REGISTRO */}
      <div className="history-section-header">
        <div>
          <h2>Elaboración de Clases, Reprocesos y Reacondicionamientos (Sección VI)</h2>
          <p style={{ margin: "4px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
            Control de transformaciones NOM-070: Abocado, Destilado con y Reacondicionamiento
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {onReloadReproceso && (
            <button
              type="button"
              onClick={handleReload}
              disabled={isRefreshingReproceso || isLocalRefreshing}
              className={`btn-refresh-custom ${(isRefreshingReproceso || isLocalRefreshing) ? "spinning" : ""}`}
              title="Actualizar tabla con los últimos registros de reprocesos"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 16px",
                background: "#ffffff",
                color: "#0f766e",
                border: "1.5px solid #99f6e4",
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "13px",
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                transition: "all 0.2s ease"
              }}
            >
              <RefreshIcon style={{ fontSize: 18 }} className={(isRefreshingReproceso || isLocalRefreshing) ? "spin-icon" : ""} />
              <span>{(isRefreshingReproceso || isLocalRefreshing) ? "Actualizando..." : "Actualizar"}</span>
            </button>
          )}
          <button 
            type="button"
            onClick={handleNewRecord}
            className="btn-add-new"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px" }}
          >
            <AddIcon style={{ fontSize: 19 }} />
            <span>Nuevo Registro Sección VI</span>
          </button>
        </div>
      </div>

      {/* TABLA PRINCIPAL DE HISTORIAL */}
      <div className="records-list-wrapper" style={{ borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
        <table className="records-table">
          <thead>
            <tr>
              <th>Fecha Ingreso</th>
              <th>Lote Origen</th>
              <th>Volumen (L)</th>
              <th>% Alc. Vol.</th>
              <th>Categoría</th>
              <th>Clase</th>
              <th>V.Bo. Inspector</th>
              <th>Estado</th>
              <th style={{ textAlign: "center" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {reprocesoRecords.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
                  <LayersIcon style={{ fontSize: 40, opacity: 0.4, marginBottom: "8px" }} />
                  <p style={{ margin: 0, fontSize: "14px" }}>No hay registros de elaboración de clases ni reprocesos almacenados.</p>
                </td>
              </tr>
            ) : (
              reprocesoRecords.map((rec, i) => {
                const rawSt = (rec.status || rec.estatus || rec.statusUI || "").toLowerCase();
                const isAprob = rawSt.includes("aprob") || rawSt.includes("acept");
                const isRev = rawSt.includes("revis") || rawSt.includes("enviad");
                const isRech = rawSt.includes("rechaz");
                const isBorr = rawSt.includes("borrador") || (!isAprob && !isRev && !isRech && rawSt === "borrador");

                const badgeClass = isAprob ? "aprobado" : isRev ? "revision" : isRech ? "rechazado" : "borrador";
                const badgeLabel = isAprob ? "Aprobado" : isRev ? "En revisión" : isRech ? "Rechazado" : "Borrador";
                const canEdit = isBorr || isRech;

                const fechaShow = rec.fechaIngreso || rec.fecha_ingreso ? String(rec.fechaIngreso || rec.fecha_ingreso).split("T")[0] : "-";
                const inspectorShow = rec.inspector || rec.nombre_inspector || (isRev ? "Enviado a revisión" : "Sin asignar");

                return (
                  <tr key={rec.id || rec.id_bitacora_reproceso || i}>
                    <td>{fechaShow}</td>
                    <td style={{ fontWeight: 800 }}>{rec.loteOrigen || rec.lote_origen || "-"}</td>
                    <td>{rec.volumenOrigen || rec.volumen_origen || 0} L</td>
                    <td>{rec.pctAlcOrigen || rec.pct_alc_origen || 0}%</td>
                    <td>{rec.categoriaOrigen || rec.categoria_origen || "Mezcal Artesanal"}</td>
                    <td>{rec.claseOrigen || rec.clase_origen || "Blanco Joven"}</td>
                    <td style={{ fontSize: "11px" }}>{inspectorShow}</td>
                    <td>
                      <span className={`badge-status ${badgeClass}`}>
                        {badgeLabel}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                        <button 
                          type="button" 
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver Detalle Completo"
                          style={{ background: "rgba(52, 152, 219, 0.15)", color: "#3498db", border: "1px solid rgba(52, 152, 219, 0.3)", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>

                        <button 
                          type="button" 
                          onClick={() => handleEditRecord(rec)}
                          disabled={!canEdit}
                          title={canEdit ? "Editar Registro" : "Solo editables en estado Borrador o Rechazado"}
                          style={{ 
                            background: canEdit ? "rgba(241, 196, 15, 0.15)" : "rgba(255,255,255,0.04)", 
                            color: canEdit ? "#f1c40f" : "var(--text-muted)", 
                            border: canEdit ? "1px solid rgba(241, 196, 15, 0.3)" : "1px solid rgba(255,255,255,0.08)", 
                            borderRadius: "6px", 
                            padding: "5px 8px", 
                            cursor: canEdit ? "pointer" : "not-allowed", 
                            display: "flex", 
                            alignItems: "center",
                            opacity: canEdit ? 1 : 0.4
                          }}
                        >
                          <EditIcon style={{ fontSize: 16 }} />
                        </button>

                        {handleDeleteReproceso && (
                          <button 
                            type="button" 
                            onClick={() => handleDeleteReproceso(rec)}
                            disabled={!canEdit}
                            title={canEdit ? "Eliminar Registro" : "Solo eliminables en estado Borrador o Rechazado"}
                            style={{ 
                              background: canEdit ? "rgba(239, 68, 68, 0.15)" : "rgba(255,255,255,0.04)", 
                              color: canEdit ? "#ef4444" : "var(--text-muted)", 
                              border: canEdit ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(255,255,255,0.08)", 
                              borderRadius: "6px", 
                              padding: "5px 8px", 
                              cursor: canEdit ? "pointer" : "not-allowed", 
                              display: "flex", 
                              alignItems: "center",
                              opacity: canEdit ? 1 : 0.4
                            }}
                          >
                            <DeleteOutlineIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* =========================================================================
          MODAL 1: FORMULARIO NUEVO REGISTRO / EDICIÓN SECCIÓN VI
      ========================================================================= */}
      {isFormOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFormOpen(false)}>
          <div
            className="modal-dialog-custom modal-xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "1000px" }}
          >
            {/* Header del Modal */}
            <div className="modal-header-custom header-theme-elaboracion">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <LayersIcon />
                </div>
                <div>
                  <h3>
                    {formData.id && reprocesoRecords.some(r => r.id === formData.id || r.id_bitacora_reproceso === formData.id)
                      ? "Editar Registro de Elaboración de Clases"
                      : "Nuevo Registro de Elaboración de Clases (Sección VI)"}
                  </h3>
                  <p>Reprocesos, abocamientos y reacondicionamientos de producto</p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsFormOpen(false)}
                title="Cerrar ventana"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Formulario en Modal */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmitForm("revision");
              }}
              className="modal-body-scrollable"
              style={{ display: "flex", flexDirection: "column", gap: "16px", background: "#F8FAFC" }}
            >
              {/* CARACTERISTICAS DEL MEZCAL ANTES DEL PROCESO */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(37, 99, 235, 0.12)", color: "#2563EB" }}>
                    <LayersIcon style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      1. Características del Mezcal Antes del Proceso
                    </h4>
                    <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>Identificación y origen del mezcal a transformar</p>
                  </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">FECHA DE INGRESO *</label>
                    <input 
                      type="date" 
                      name="fechaIngreso" 
                      required
                      value={formData?.fechaIngreso || ""} 
                      onChange={handleChange} 
                      className="integrated-input-control"
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">LOTE ORIGEN (GRANELES ENVASADO)</label>
                    <select 
                      name="loteOrigen" 
                      value={formData?.loteOrigen || ""} 
                      onChange={handleLoteOrigenChange}
                      className="integrated-select-control"
                    >
                      <option value="">-- Seleccionar Lote Granel (Envasado) --</option>
                      {availableGraneles.map((r, idx) => {
                        const lNo = r.noLote || r.no_lote || `ID-${r.id_bitacora_granel}`;
                        const tNo = r.noTanque || r.no_tanque || "Tanque";
                        const volDisp = r._vDisp ?? (parseFloat(String(r.invFinalVol ?? r.saldoFinal ?? r.litros ?? r.volumen ?? 0).replace(/[^\d.]/g, '')) || 0);
                        return (
                          <option key={idx} value={lNo}>
                            {lNo} (Tanque: {tNo} - Disp: {volDisp} L)
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">VOLUMEN INICIAL *</label>
                    <input 
                      type="number" 
                      name="volumenOrigen" 
                      required
                      placeholder="Ej. 495"
                      value={formData?.volumenOrigen || ""} 
                      onChange={handleChange} 
                      className="integrated-input-control"
                      style={{ paddingRight: "36px" }}
                    />
                    <span className="integrated-input-suffix">L</span>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">% ALC. VOL. INICIAL</label>
                    <input 
                      type="number" 
                      step="0.1"
                      name="pctAlcOrigen" 
                      placeholder="Ej. 47.0"
                      value={formData?.pctAlcOrigen || ""} 
                      onChange={handleChange} 
                      className="integrated-input-control"
                      style={{ paddingRight: "36px" }}
                    />
                    <span className="integrated-input-suffix">%</span>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "10px", marginTop: "10px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">CATEGORÍA ORIGEN</label>
                    <select 
                      name="categoriaOrigen" 
                      value={formData?.categoriaOrigen || "Mezcal Artesanal"} 
                      onChange={handleChange}
                      className="integrated-select-control"
                    >
                      <option value="Mezcal">Mezcal</option>
                      <option value="Mezcal Artesanal">Mezcal Artesanal</option>
                      <option value="Mezcal Ancestral">Mezcal Ancestral</option>
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">CLASE ORIGEN</label>
                    <input 
                      type="text" 
                      name="claseOrigen" 
                      value={formData?.claseOrigen || ""} 
                      onChange={handleChange} 
                      placeholder="Ej. Joven / Blanco"
                      className="integrated-input-control"
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">TIPO DE MAGUEY</label>
                    <input 
                      type="text" 
                      name="tipoMagueyOrigen" 
                      value={formData?.tipoMagueyOrigen || ""} 
                      onChange={handleChange} 
                      placeholder="Ej. Espadín"
                      className="integrated-input-control"
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">Nº ANÁLISIS FQ</label>
                    <input 
                      type="text" 
                      name="analisisOrigen" 
                      value={formData?.analisisOrigen || ""} 
                      onChange={handleChange} 
                      placeholder="Ej. ANA-2026-098"
                      className="integrated-input-control"
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px", marginTop: "10px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">INGREDIENTES INICIALES</label>
                    <input 
                      type="text" 
                      name="ingredientesOrigen" 
                      value={formData?.ingredientesOrigen || ""} 
                      onChange={handleChange} 
                      placeholder="Ej. Maguey Espadín, agua"
                      className="integrated-input-control"
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">EDAD ORIGEN</label>
                    <input 
                      type="text" 
                      name="edadOrigen" 
                      value={formData?.edadOrigen || ""} 
                      onChange={handleChange} 
                      placeholder="Ej. Sin edad / 0 meses"
                      className="integrated-input-control"
                    />
                  </div>
                </div>
              </section>

              {/* PROCESAMIENTO DEL MEZCAL */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(210, 105, 30, 0.12)", color: "var(--copper)" }}>
                    <WaterDropIcon style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      2. Procesamiento del Mezcal
                    </h4>
                    <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>Modificaciones, aditivos e ingredientes incorporados</p>
                  </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">PROCESO REALIZADO *</label>
                    <select 
                      name="procesoRealizado" 
                      value={formData?.procesoRealizado || "ABOCADO CON"} 
                      onChange={handleChange}
                      className="integrated-select-control"
                      style={{ fontWeight: 700, color: "var(--copper)" }}
                    >
                      <option value="ABOCADO CON">ABOCADO CON</option>
                      <option value="DESTILADO CON">DESTILADO CON</option>
                      <option value="REPROCESO O REACONDICIONAMIENTO">REPROCESO O REACONDICIONAMIENTO</option>
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">INGREDIENTES ADICIONADOS (ORDEN CUANTITATIVO)</label>
                    <input 
                      type="text" 
                      name="ingredientesAdicionados" 
                      value={formData?.ingredientesAdicionados || ""} 
                      onChange={handleChange} 
                      className="integrated-input-control"
                      placeholder="Ej: Hierbabuena, cáscara de naranja, frutas..."
                    />
                  </div>
                </div>

                <div className="integrated-input-box" style={{ marginTop: "10px", minHeight: "75px", justifyContent: "flex-start", padding: "8px 14px" }}>
                  <label className="integrated-input-label">DESCRIPCIÓN BREVE DEL PROCESO</label>
                  <textarea 
                    name="descripcionProceso" 
                    value={formData?.descripcionProceso || ""} 
                    onChange={handleChange} 
                    className="integrated-input-control"
                    style={{ height: "42px", resize: "none" }}
                    placeholder="Detalles sobre temperatura, tiempo de contacto o maceración..."
                  />
                </div>
              </section>

              {/* RESULTADO DEL PROCESO */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(16, 185, 129, 0.12)", color: "#10B981" }}>
                    <ScienceIcon style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      3. Resultado del Proceso
                    </h4>
                    <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>Características del lote final obtenido tras el proceso</p>
                  </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">FECHA DE SALIDA</label>
                    <input 
                      type="date" 
                      name="fechaSalida" 
                      value={formData?.fechaSalida || ""} 
                      onChange={handleChange} 
                      className="integrated-input-control"
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">LOTE FINAL OBTENIDO *</label>
                    <input 
                      type="text" 
                      name="loteSalida" 
                      required
                      value={formData?.loteSalida || ""} 
                      onChange={handleChange} 
                      placeholder="Ej. ENV-2026-001-ABO"
                      className="integrated-input-control"
                      style={{ fontWeight: 700, color: "var(--agave-green)" }}
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">VOLUMEN FINAL *</label>
                    <input 
                      type="number" 
                      name="volumenSalida" 
                      required
                      placeholder="Ej. 490"
                      value={formData?.volumenSalida || ""} 
                      onChange={handleChange} 
                      className="integrated-input-control"
                      style={{ paddingRight: "36px" }}
                    />
                    <span className="integrated-input-suffix">L</span>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">% ALC. VOL. FINAL</label>
                    <input 
                      type="number" 
                      step="0.1"
                      name="pctAlcSalida" 
                      placeholder="Ej. 46.5"
                      value={formData?.pctAlcSalida || ""} 
                      onChange={handleChange} 
                      className="integrated-input-control"
                      style={{ paddingRight: "36px" }}
                    />
                    <span className="integrated-input-suffix">%</span>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">CLASE FINAL OBTENIDA (NOM-070) *</label>
                    <input 
                      type="text" 
                      name="claseSalida" 
                      readOnly
                      value={formData?.claseSalida || obtenerClaseFinalSegunProceso(formData?.procesoRealizado || "ABOCADO CON", formData?.claseOrigen)} 
                      className="integrated-input-control"
                      style={{ fontWeight: 800, color: "var(--copper)", background: "rgba(210, 105, 30, 0.05)", cursor: "not-allowed" }}
                    />
                    <small style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>
                      Definida por el proceso: {formData?.procesoRealizado || "ABOCADO CON"}
                    </small>
                  </div>
                </div>

                <div className="integrated-input-box" style={{ marginTop: "10px", minHeight: "75px", justifyContent: "flex-start", padding: "8px 14px" }}>
                  <label className="integrated-input-label">OBSERVACIONES</label>
                  <textarea 
                    name="observaciones" 
                    value={formData?.observaciones || ""} 
                    onChange={handleChange} 
                    className="integrated-input-control"
                    style={{ height: "42px", resize: "none" }}
                    placeholder="Observaciones generales o instrucciones para el inspector..."
                  />
                </div>
              </section>

              {/* Botones del Modal */}
              <div className="modal-footer-custom" style={{ marginTop: "6px", borderRadius: "12px", background: "#FFFFFF", padding: "14px 20px" }}>
                <button
                  type="button"
                  className="btn-cancel-modal"
                  onClick={() => setIsFormOpen(false)}
                >
                  Cancelar
                </button>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    className="btn-secondary-custom"
                    style={{ padding: "9px 18px", fontSize: "12.5px" }}
                    onClick={() => handleSubmitForm("borrador")}
                  >
                    Guardar Borrador
                  </button>
                  <button
                    type="submit"
                    className="btn-submit-modal"
                    style={{ background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)", boxShadow: "0 4px 14px rgba(37, 99, 235, 0.35)" }}
                  >
                    <CheckCircleIcon style={{ fontSize: 18 }} />
                    <span>{formData.id && reprocesoRecords.some(r => r.id === formData.id || r.id_bitacora_reproceso === formData.id) && ((formData.status || formData.estatus) !== "borrador") ? "Guardar Cambios" : "Registrar Sección VI"}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 2: DETALLE COMPLETO DE REPROCESO / ELABORACIÓN
      ========================================================================= */}
      {selectedRecord && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setSelectedRecord(null)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "750px" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-elaboracion">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <LayersIcon />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: "white" }}>
                    Lote Origen: {selectedRecord.loteOrigen || selectedRecord.lote_origen || "-"} → Final: {selectedRecord.loteSalida || selectedRecord.lote_salida || "-"}
                  </h3>
                  <p style={{ margin: "2px 0 0 0", color: "rgba(255,255,255,0.85)", fontSize: "12px" }}>
                    Detalle de Elaboración de Clases y Reprocesos en Envasado (Sección VI)
                  </p>
                </div>
              </div>
              <button 
                type="button"
                className="modal-close-btn"
                onClick={() => setSelectedRecord(null)}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Contenido del Detalle */}
            <div className="detail-modal-body" style={{ padding: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "12px", display: "block" }}>Proceso Realizado:</span>
                  <span style={{ color: "var(--copper)", fontSize: "16px", fontWeight: 800 }}>{selectedRecord.procesoRealizado || selectedRecord.proceso_realizado || "ABOCADO CON"}</span>
                </div>
                {(() => {
                  const selSt = (selectedRecord.status || selectedRecord.estatus || selectedRecord.statusUI || "").toLowerCase();
                  const isSelAprob = selSt.includes("aprob") || selSt.includes("acept");
                  const isSelRev = selSt.includes("revis") || selSt.includes("enviad");
                  const isSelRech = selSt.includes("rechaz");
                  const selBadgeClass = isSelAprob ? "aprobado" : isSelRev ? "revision" : isSelRech ? "rechazado" : "borrador";
                  const selBadgeLabel = isSelAprob ? "Aprobado" : isSelRev ? "En revisión" : isSelRech ? "Rechazado" : "Borrador";
                  return (
                    <span className={`badge-status ${selBadgeClass}`}>
                      {selBadgeLabel}
                    </span>
                  );
                })()}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", fontSize: "13.5px" }}>
                <div><strong>Fecha Ingreso:</strong> {selectedRecord.fechaIngreso || selectedRecord.fecha_ingreso ? String(selectedRecord.fechaIngreso || selectedRecord.fecha_ingreso).split("T")[0] : "-"}</div>
                <div><strong>Volumen Origen:</strong> {selectedRecord.volumenOrigen || selectedRecord.volumen_origen || 0} L ({selectedRecord.pctAlcOrigen || selectedRecord.pct_alc_origen || 0}%)</div>
                <div><strong>Categoría Origen:</strong> {selectedRecord.categoriaOrigen || selectedRecord.categoria_origen || "Mezcal Artesanal"}</div>
                <div><strong>Clase Origen:</strong> {selectedRecord.claseOrigen || selectedRecord.clase_origen || "Blanco Joven"}</div>
                <div><strong>Tipo Maguey:</strong> {selectedRecord.tipoMagueyOrigen || selectedRecord.tipo_maguey_origen || "N/A"}</div>
                <div><strong>Nº Análisis:</strong> {selectedRecord.analisisOrigen || selectedRecord.analisis_origen || "N/A"}</div>
                <div><strong>Ingredientes Origen:</strong> {selectedRecord.ingredientesOrigen || selectedRecord.ingredientes_origen || "N/A"}</div>
                <div><strong>Ingredientes Adicionados:</strong> {selectedRecord.ingredientesAdicionados || selectedRecord.ingredientes_adicionados || "Ninguno"}</div>
                <div><strong>Fecha Salida:</strong> {selectedRecord.fechaSalida || selectedRecord.fecha_salida ? String(selectedRecord.fechaSalida || selectedRecord.fecha_salida).split("T")[0] : "-"}</div>
                <div><strong>Lote Final:</strong> <span style={{ color: "var(--agave-green)", fontWeight: 800 }}>{selectedRecord.loteSalida || selectedRecord.lote_salida || "-"}</span></div>
                <div><strong>Volumen Final:</strong> <span style={{ color: "#2ecc71", fontWeight: 800 }}>{selectedRecord.volumenSalida || selectedRecord.volumen_salida || 0} L ({selectedRecord.pctAlcSalida || selectedRecord.pct_alc_salida || 0}%)</span></div>
                <div><strong>Clase Final:</strong> {selectedRecord.claseSalida || selectedRecord.clase_salida || "N/A"}</div>
                <div style={{ gridColumn: "1 / -1" }}><strong>Inspector Responsable:</strong> {selectedRecord.inspector || selectedRecord.nombre_inspector || "Pendiente V.Bo."}</div>
              </div>

              <div style={{ marginTop: "18px", paddingTop: "14px", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                <strong>Descripción del Proceso:</strong>
                <p style={{ margin: "6px 0 14px 0", color: "#64748B", fontSize: "13px" }}>
                  {selectedRecord.descripcionProceso || selectedRecord.descripcion_proceso || "Sin descripción registrada."}
                </p>
                <strong>Observaciones:</strong>
                <p style={{ margin: "6px 0 0 0", color: "#64748B", fontSize: "13px" }}>
                  {selectedRecord.observaciones || "Sin observaciones."}
                </p>
                {selectedRecord.observaciones_correccion && (
                  <div style={{ marginTop: "10px", padding: "10px", background: "#FEF2F2", borderRadius: "8px", border: "1px solid #FCA5A5", color: "#991B1B" }}>
                    <strong>Observaciones del Inspector:</strong>
                    <p style={{ margin: "4px 0 0 0", fontSize: "12.5px" }}>{selectedRecord.observaciones_correccion}</p>
                  </div>
                )}
              </div>

              <div className="modal-footer-custom" style={{ marginTop: "20px", borderRadius: "10px" }}>
                <button 
                  type="button"
                  onClick={() => setSelectedRecord(null)} 
                  className="btn-cancel-modal"
                >
                  Cerrar
                </button>
                {((selectedRecord.status || selectedRecord.estatus || selectedRecord.statusUI || "").toLowerCase().includes("borrador") || (selectedRecord.status || selectedRecord.estatus || selectedRecord.statusUI || "").toLowerCase().includes("rechaz")) && (
                  <button 
                    type="button"
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleEditRecord(rec);
                    }}
                    className="btn-submit-modal"
                    style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", boxShadow: "0 4px 14px rgba(245, 158, 11, 0.3)" }}
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                    <span>Editar Registro</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ReprocesoSection;
